import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { FloatingMenu } from "@/components/ui/FloatingMenu";
import ContractorIcon from "../assets/ContractorIcon";
import HomeownerIcon from "../assets/HomeownerIcon";
import { JOIN_AS_PRO_URL } from "../../../constants/urls";
import type { MeUser } from "../../../../../hooks/queries/useMe";

export default function GuestLoginMenu({
  user,
  variant = "desktop",
  onClose,
}: GuestLoginMenuProps) {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const isMobile = variant === "mobile";
  const showJoinAsPro =
    isMobile || !user?.isLoggedIn || user.userType === "homeowner";

  const loginButton = (
    <Button
      variant="bordered"
      color="default"
      radius="md"
      size="lg"
      className={
        isMobile
          ? "h-11 w-full border-1 border-default-200 text-base"
          : "h-12 text-base xl:text-lg border-1 border-default-200"
      }
      onPress={isMobile ? () => setIsLoginOpen(!isLoginOpen) : undefined}
    >
      Login
    </Button>
  );

  const loginMenu = isMobile ? (
    <FloatingMenu
      className="relative flex-1 self-start"
      isOpen={isLoginOpen}
      onOpenChange={setIsLoginOpen}
    >
      {loginButton}
      <LoginMenuContent onNavigate={onClose} />
    </FloatingMenu>
  ) : (
    <FloatingMenu>
      <FloatingMenu.Trigger>{loginButton}</FloatingMenu.Trigger>
      <LoginMenuContent />
    </FloatingMenu>
  );

  const joinAsProButton = (
    <Button
      color="primary"
      radius={isMobile ? "lg" : "md"}
      className={
        isMobile
          ? "h-11 w-full text-base"
          : "h-12 border-1 text-base xl:text-lg"
      }
      onPress={() => window.location.href = JOIN_AS_PRO_URL}
    >
      Join As Pro
    </Button>
  );

  if (isMobile) {
    return (
      <div className="flex items-start gap-4 px-4 py-5">
        {loginMenu}
        {showJoinAsPro && <div className="flex-1">{joinAsProButton}</div>}
      </div>
    );
  }

  return (
    <>
      {loginMenu}
      {showJoinAsPro && joinAsProButton}
    </>
  );
}

function LoginMenuContent({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <FloatingMenu.Content
      width="w-[220px]"
      classNames={{
        arrow: "sm:left-1/2 sm:-translate-x-1/2",
        card: "rounded-xl p-3 shadow-[0_5px_20px_rgb(0_0_0_/_25%)]",
      }}
    >
      <a
        href="/login"
        className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold !text-primary transition-colors hover:bg-default-100 !no-underline"
        onClick={onNavigate}
      >
        <ContractorIcon className="h-5 w-5 shrink-0 text-primary" />
        <span>Pros Login</span>
      </a>
      <a
        href="/homeowner-login"
        className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold !text-primary transition-colors hover:bg-default-100 !no-underline"
        onClick={onNavigate}
      >
        <HomeownerIcon className="h-5 w-5 shrink-0 text-primary" />
        <span>Homeowner Login</span>
      </a>
    </FloatingMenu.Content>
  );
}

interface GuestLoginMenuProps {
  user?: MeUser;
  variant?: "desktop" | "mobile";
  onClose?: () => void;
}
